// История как данные: строки журнала → действия (по транзакции), подписи и человеческие значения,
// что записать для отката и восстановления, кому можно. Чистые функции; экран только рисует и зовёт.
import { canWriteTable, isWriter } from '@core/auth/roles';
import { dateTimeLabel, longDate, type ISODate } from '@core/dates';
import type { Change, Op, TableName } from '@core/store/model';
import { ACTION_LABEL, DATE_FIELDS, FIELD_LABEL, ORDER_FIELD, PARENTS, REF_FIELDS, TABLE_LABEL, TIMESTAMP_FIELDS, VALUE_LABEL, isHiddenField } from './labels';

export type Kind = 'insert' | 'update' | 'delete';
export type HistoryRow = {
  id: number; at: string; tx: string; actor: string | null; op: Op | null;
  table_name: string; row_id: string; kind: Kind; changed: string[];
  old: Record<string, unknown> | null; new: Record<string, unknown> | null; row_label: string | null;
};

export type Action = { tx: string; at: string; actor: string | null; op: Op | null; rows: HistoryRow[]; label: string };

// ---------------------------------------------------------------- действия

/** Подпись действия: по op.kind из словаря, иначе по виду строк. */
export function actionLabel(op: Op | null, rows: HistoryRow[]): string {
  const known = op ? ACTION_LABEL[op.kind] : undefined;
  if (typeof known === 'function') return known(rows.length);
  if (known) return known;
  const one = rows[0];
  if (rows.length === 1 && one) {
    const what = (TABLE_LABEL[one.table_name] ?? one.table_name).toLowerCase();
    return one.kind === 'insert' ? `created a ${what}` : one.kind === 'delete' ? `deleted a ${what}` : `changed a ${what}`;
  }
  return `changed ${rows.length} rows`;
}

/** Строки (новые сверху) → действия по транзакции, порядок тот же; строки внутри — от новых к старым. */
export function groupActions(rows: HistoryRow[]): Action[] {
  const out: Action[] = [];
  let cur: Action | null = null;
  for (const r of rows) {
    if (cur && cur.tx === r.tx) { cur.rows.push(r); continue; }
    cur = { tx: r.tx, at: r.at, actor: r.actor, op: r.op, rows: [r], label: '' };
    out.push(cur);
  }
  for (const a of out) {
    a.op = a.rows.find((r) => r.op)?.op ?? null;
    a.at = a.rows.reduce((m, r) => (r.at > m ? r.at : m), a.rows[0]!.at);
    a.label = actionLabel(a.op, a.rows);
  }
  return out;
}

// ---------------------------------------------------------------- подписи

/** Название по id из живой таблицы; null — объекта нет (удалён). */
export type Resolve = (table: string, id: string) => string | null;

export const fieldLabel = (field: string): string => FIELD_LABEL[field] ?? field.replace(/_/g, ' ');

export function formatValue(field: string, value: unknown, resolve: Resolve): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  const coded = VALUE_LABEL[field]?.[String(value)];
  if (coded) return coded;
  const ref = REF_FIELDS[field];
  if (ref) return resolve(ref, String(value)) ?? '(deleted)';
  if (DATE_FIELDS.has(field) && typeof value === 'string') return longDate(value as ISODate);
  if (TIMESTAMP_FIELDS.has(field) && typeof value === 'string') return dateTimeLabel(value);
  if (field === 'rule' && typeof value === 'object') { const r = value as { every?: number; unit?: string }; return r.every && r.unit ? `every ${r.every === 1 ? r.unit : `${r.every} ${r.unit}s`}` : 'repeat rule'; }
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

export type FieldChange = { field: string; label: string; from: string; to: string };
export type RowSummary = { title: string; fields: FieldChange[]; reordered: boolean };

/** Строка журнала для глаз: что за объект, какие поля с какого на какое (insert — главные поля, delete — ничего). */
const LABEL_MAX = 80; // текст вопроса как подпись строки — целиком он на три строки
const clip = (s: string): string => (s.length > LABEL_MAX ? `${s.slice(0, LABEL_MAX - 1).trimEnd()}…` : s);

export function rowSummary(row: HistoryRow, resolve: Resolve): RowSummary {
  const kindLabel = TABLE_LABEL[row.table_name] ?? row.table_name;
  const src = row.new ?? row.old ?? {};
  const refs = Object.keys(src).filter((f) => REF_FIELDS[f] && src[f]).map((f) => formatValue(f, src[f], resolve)); // в порядке колонок строки
  const title = row.row_label ? `${kindLabel} «${clip(row.row_label)}»` : refs.length ? `${kindLabel}: ${refs.join(' · ')}` : kindLabel;
  const shown = row.changed.filter((f) => !isHiddenField(f) && f !== ORDER_FIELD);
  const reordered = row.kind === 'update' && shown.length === 0 && row.changed.includes(ORDER_FIELD);
  if (row.kind === 'delete') return { title, fields: [], reordered: false };
  const fields = shown
    // у создания — только заполненные поля и без того, что уже стоит в названии строки
    .filter((f) => row.kind === 'update' || (row.new?.[f] !== null && row.new?.[f] !== undefined && row.new?.[f] !== '' && row.new?.[f] !== false))
    .filter((f) => row.kind === 'update' || String(row.new?.[f]) !== row.row_label)
    .map((f) => ({ field: f, label: fieldLabel(f), from: row.kind === 'update' ? formatValue(f, row.old?.[f], resolve) : '', to: formatValue(f, row.new?.[f], resolve) }));
  return { title, fields, reordered };
}

export type PresentedRow = { row: HistoryRow; summary: RowSummary; group: HistoryRow[] };

/**
 * Строки действия для глаз: назначения (task_assignee) одним полем «Assignees: + Max · − Ilya» на строке
 * задачи того же действия, а без неё — своей строкой с названием задачи. group — что откатывать
 * вместе: у строки с назначениями это все её строки журнала.
 */
export function presentRows(action: Action, resolve: Resolve): PresentedRow[] {
  const out: PresentedRow[] = action.rows.filter((r) => r.table_name !== 'task_assignee').map((r) => ({ row: r, summary: rowSummary(r, resolve), group: [r] }));
  const byTask = new Map<string, HistoryRow[]>();
  for (const r of action.rows) {
    if (r.table_name !== 'task_assignee') continue;
    const taskId = String((r.new ?? r.old)?.['task_id'] ?? '');
    byTask.set(taskId, [...(byTask.get(taskId) ?? []), r]);
  }
  for (const [taskId, rows] of byTask) {
    const name = (r: HistoryRow) => formatValue('person_id', (r.new ?? r.old)?.['person_id'], resolve);
    const added = rows.filter((r) => r.kind === 'insert').map(name);
    const removed = rows.filter((r) => r.kind === 'delete').map(name);
    const value = [...added.map((n) => `+ ${n}`), ...removed.map((n) => `− ${n}`)].join(' · ') || 'reordered';
    const field: FieldChange = { field: 'assignees', label: 'Assignees', from: '', to: value };
    const host = out.find((p) => p.row.table_name === 'task' && p.row.row_id === taskId && p.row.kind !== 'delete');
    if (host) { host.summary.fields.push(field); host.group.push(...rows); continue; }
    const first = rows[0]!;
    const label = resolve('task', taskId);
    const row: HistoryRow = { ...first, table_name: 'task', row_id: taskId, kind: 'update', changed: ['assignees'], row_label: label };
    out.push({ row, summary: { title: `Task «${label ?? '(deleted)'}»`, fields: [field], reordered: false }, group: rows });
  }
  return out;
}

// ---------------------------------------------------------------- откат

const AUDIT = new Set(['created_at', 'created_by', 'updated_at', 'updated_by']);
const stripAudit = (r: Record<string, unknown>): Record<string, unknown> => Object.fromEntries(Object.entries(r).filter(([k]) => !AUDIT.has(k)));

/** Глубина таблицы в дереве родителей: родитель вставляется раньше детей при восстановлении. */
export function depthOf(table: string, seen = new Set<string>()): number {
  const parents = PARENTS[table];
  if (!parents || seen.has(table)) return 0;
  seen.add(table);
  return 1 + Math.max(0, ...parents.map((p) => depthOf(p, seen)));
}

/** Ссылка на откаченную строку: номер для точного перехода, автор и время — чтобы бейдж читался без него. */
const undoOp = (kind: 'revert' | 'restore', row: HistoryRow): Op => ({ kind, log_id: row.id, undo_at: row.at, undo_actor: row.actor });

export type Live = { updated_at: string } | null | undefined;
export type Planned = { changes: Change[]; op: Op } | { error: string };

/**
 * Откат одной строки журнала: update — вернуть изменённые поля с expect_updated_at живой строки; insert — удалить;
 * delete — вставить старую строку и всё, что каскад унёс в той же транзакции (sameTx: delete-строки того же tx).
 */
export function planRevert(row: HistoryRow, live: Live, sameTx: HistoryRow[] = []): Planned {
  const table = row.table_name as TableName;
  if (row.kind === 'update') {
    if (!live) return { error: 'The object was deleted since; restore it from the Deleted filter first' };
    return { changes: [{ table, id: row.row_id, set: row.old ?? {}, expect_updated_at: live.updated_at }], op: undoOp('revert', row) };
  }
  if (row.kind === 'insert') {
    if (!live) return { error: 'Already deleted' };
    return { changes: [{ table, id: row.row_id, delete: true }], op: undoOp('revert', row) };
  }
  if (live) return { error: 'Already restored' };
  const rows = [row, ...sameTx.filter((r) => r.kind === 'delete' && r.id !== row.id)]
    .sort((a, b) => depthOf(a.table_name) - depthOf(b.table_name) || a.id - b.id);
  return { changes: rows.map((r) => ({ table: r.table_name as TableName, insert: stripAudit(r.old ?? {}) })), op: { ...undoOp('restore', row), rows: rows.length } };
}

/** Откат действия целиком: правки и вставки в обратном порядке, удалённые строки — одним восстановлением. */
export function planRevertAction(action: Action, liveOf: (row: HistoryRow) => Live): Planned {
  const changes: Change[] = [];
  const deletes = action.rows.filter((r) => r.kind === 'delete');
  for (const r of action.rows.filter((r) => r.kind !== 'delete')) { // rows уже от новых к старым — это и есть обратный порядок
    const p = planRevert(r, liveOf(r));
    if ('error' in p) return { error: `${TABLE_LABEL[r.table_name] ?? r.table_name} «${r.row_label ?? r.row_id}»: ${p.error}` };
    changes.push(...p.changes);
  }
  if (deletes.length) {
    const root = deletes[deletes.length - 1]!; // самая ранняя delete-строка транзакции — корень каскада
    const p = planRevert(root, liveOf(root), deletes);
    if ('error' in p) return p;
    changes.push(...p.changes);
  }
  return { changes, op: { kind: 'revert', tx: action.tx, rows: action.rows.length, undo_at: action.at, undo_actor: action.actor } };
}

/**
 * Кому можно: любые правки участнику и админу — ту же правку он повторит руками, кнопка лишь
 * избавляет от возни; наблюдателю нельзя, он вообще не пишет план. Импорт не откатывается:
 * за одной его строкой стоят тысячи, и «вернуть как было» она не вернёт.
 */
export function canRevert(row: HistoryRow, me: { id: string; role: string } | null): { ok: true } | { ok: false; why: string } {
  if (!me) return { ok: false, why: 'Sign in to revert' };
  if (row.op?.kind === 'migrate') return { ok: false, why: 'Imported rows are not reverted' };
  if (!isWriter(me.role)) return { ok: false, why: 'Viewers cannot change the plan' };
  // справочники и доступ участник не пишет — база откажет, кнопку гасим здесь, до вопросов о поздних правках
  if (!canWriteTable(me.role, row.table_name)) return { ok: false, why: 'Admin only: people, teams, accounts' };
  return { ok: true };
}
